import { z } from 'zod';
import { KPI_AGGREGATIONS } from './kpis';
import { PROJECTION_METHODS } from './planned';

/**
 * Planejamento de cenários (T-180, RF-28, RF-14, PRD §11.16; DEC-063): cenários por projeto com premissas e metas por
 * KPI e período, comparados com o realizado e a projeção atual; o cenário base vira as metas oficiais.
 */

export const SCENARIO_KINDS = ['PESSIMISTIC', 'REALISTIC', 'OPTIMISTIC', 'CUSTOM'] as const;
export type ScenarioKindName = (typeof SCENARIO_KINDS)[number];
export const SCENARIO_KIND_LABELS: Record<ScenarioKindName, string> = {
  PESSIMISTIC: 'Pessimista',
  REALISTIC: 'Realista',
  OPTIMISTIC: 'Otimista',
  CUSTOM: 'Outro',
};
/** Os três cenários iniciais de todo projeto, nesta ordem (§11.16). */
export const DEFAULT_SCENARIOS: readonly { kind: Exclude<ScenarioKindName, 'CUSTOM'>; name: string }[] = [
  { kind: 'PESSIMISTIC', name: 'Pessimista' },
  { kind: 'REALISTIC', name: 'Realista' },
  { kind: 'OPTIMISTIC', name: 'Otimista' },
];

/** Metas do cenário: do mês ou do ano (os mesmos tipos das metas oficiais da empresa). */
export const SCENARIO_PERIOD_TYPES = ['MONTH', 'YEAR'] as const;
export type ScenarioPeriodTypeName = (typeof SCENARIO_PERIOD_TYPES)[number];

export const SCENARIO_PREMISES_MAX = 50;

const optionalText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} pode ter no máximo ${max} caracteres.`)
    .nullable()
    .optional()
    .transform((v) => v || null);

export const scenarioInputSchema = z.object({
  name: z
    .string({ message: 'Informe o nome do cenário.' })
    .trim()
    .min(2, 'Informe o nome do cenário.')
    .max(80, 'Nome longo demais (até 80 caracteres).'),
  description: optionalText(1000, 'A descrição'),
});
export type ScenarioInput = z.input<typeof scenarioInputSchema>;

/** Premissas do cenário (substitui a lista; mantém o id das que ficam). */
export const scenarioPremisesInputSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().uuid().optional(),
        text: z
          .string({ message: 'Escreva a premissa.' })
          .trim()
          .min(3, 'Escreva a premissa (pelo menos 3 caracteres).')
          .max(500, 'Premissa longa demais (até 500 caracteres).'),
      }),
    )
    .max(SCENARIO_PREMISES_MAX, `No máximo ${SCENARIO_PREMISES_MAX} premissas por cenário.`),
});
export type ScenarioPremisesInput = z.input<typeof scenarioPremisesInputSchema>;

/** Metas do cenário num ano: cada célula é KPI × (mês 1–12 ou o ano); valor nulo remove a célula. */
export const scenarioTargetsInputSchema = z.object({
  year: z.number().int(),
  cells: z
    .array(
      z
        .object({
          kpiId: z.string().uuid(),
          periodType: z.enum(SCENARIO_PERIOD_TYPES),
          period: z.number().int(),
          value: z.number({ message: 'Informe um número.' }).finite().nullable(),
        })
        .refine((c) => (c.periodType === 'YEAR' ? c.period === 1 : c.period >= 1 && c.period <= 12), {
          message: 'Período inválido: mês de 1 a 12, ou 1 para o ano.',
          path: ['period'],
        }),
    )
    .min(1, 'Informe ao menos uma meta.')
    .max(2000, 'Metas demais num só envio.'),
});
export type ScenarioTargetsInput = z.input<typeof scenarioTargetsInputSchema>;

/** Escolher o cenário base: as metas dele viram as oficiais (justificativa para as que mudam — DEC-063). */
export const scenarioBaseInputSchema = z.object({
  reason: z
    .string()
    .trim()
    .max(500, 'Justificativa longa demais (até 500 caracteres).')
    .nullable()
    .optional()
    .transform((v) => v || null),
});
export type ScenarioBaseInput = z.input<typeof scenarioBaseInputSchema>;

export const scenarioSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  kind: z.enum(SCENARIO_KINDS),
  description: z.string().nullable(),
  isBase: z.boolean(),
  /** Quando virou o base e quem escolheu. */
  baseSetAt: z.string().nullable(),
  baseSetByName: z.string().nullable(),
  premises: z.array(z.object({ id: z.string().uuid(), text: z.string() })),
  /** Quantas metas o cenário tem (todas as células). */
  targetCount: z.number().int(),
  archivedAt: z.string().nullable(),
  updatedAt: z.string(),
});
export type Scenario = z.infer<typeof scenarioSchema>;
export const scenarioListSchema = z.object({ items: z.array(scenarioSchema) });

/** Escolher o base: o cenário e o que foi gravado nas metas oficiais (com o fluxo de aprovação, pendentes). */
export const scenarioBaseResultSchema = z.object({
  scenario: scenarioSchema,
  applied: z.object({
    created: z.number().int(),
    revised: z.number().int(),
    removed: z.number().int(),
    unchanged: z.number().int(),
    pendingApproval: z.boolean(),
  }),
});
export type ScenarioBaseResult = z.infer<typeof scenarioBaseResultSchema>;
export type ScenarioList = z.infer<typeof scenarioListSchema>;

export const scenarioTargetCellSchema = z.object({
  kpiId: z.string().uuid(),
  periodType: z.enum(SCENARIO_PERIOD_TYPES),
  period: z.number().int(),
  value: z.number(),
});
export type ScenarioTargetCell = z.infer<typeof scenarioTargetCellSchema>;
export const scenarioTargetsSchema = z.object({
  year: z.number().int(),
  cells: z.array(scenarioTargetCellSchema),
});
export type ScenarioTargets = z.infer<typeof scenarioTargetsSchema>;

/** Opções da tela: os anos do horizonte e os KPIs ativos no escopo (com a agregação do valor anual). */
export const scenarioOptionsSchema = z.object({
  years: z.array(z.number().int()),
  kpis: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      unitLabel: z.string(),
      aggregation: z.enum(KPI_AGGREGATIONS),
    }),
  ),
});
export type ScenarioOptions = z.infer<typeof scenarioOptionsSchema>;

export const scenarioComparisonQuerySchema = z.object({
  kpiId: z.string().uuid(),
  year: z.coerce.number().int(),
});

/** Uma linha da comparação: valor anual e os 12 meses (null onde não há). */
const comparisonLine = z.object({ annual: z.number().nullable(), months: z.array(z.number().nullable()) });
export const scenarioComparisonSchema = z.object({
  kpi: z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    unitLabel: z.string(),
    aggregation: z.enum(KPI_AGGREGATIONS),
  }),
  year: z.number().int(),
  scenarios: z.array(
    comparisonLine.extend({
      id: z.string().uuid(),
      name: z.string(),
      kind: z.enum(SCENARIO_KINDS),
      isBase: z.boolean(),
    }),
  ),
  /** "Real / realizado": o realizado do ano (acumulado até o último mês com realizado). */
  actual: comparisonLine.extend({ lastActualMonth: z.number().int().nullable() }),
  /** "Projeção atual": o fechamento projetado (tendência; média com um só mês de realizado — DEC-063). */
  projection: comparisonLine.extend({ method: z.enum(PROJECTION_METHODS).nullable() }),
  /** Meta oficial do ano (a vigente aprovada), para referência. */
  officialAnnual: z.number().nullable(),
});
export type ScenarioComparison = z.infer<typeof scenarioComparisonSchema>;

/** Histórico paginado por mudança: `before` é o cursor "instante|id" da última mudança da página anterior. */
export const SCENARIO_HISTORY_PAGE = 20;
export const scenarioHistoryQuerySchema = z.object({
  /** Cursor "instante|id da mudança" (o desempate entre mudanças do mesmo instante). */
  before: z
    .string()
    .regex(
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z\|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      'Cursor do histórico inválido.',
    )
    .refine((v) => {
      const at = new Date(v.split('|')[0]!);
      // Data impossível (ex.: mês 13) não vira outro dia: o instante precisa voltar igual.
      return !Number.isNaN(at.getTime()) && at.toISOString().slice(0, 19) === v.slice(0, 19);
    }, 'Cursor do histórico inválido.')
    .optional(),
});
export const scenarioHistorySchema = z.object({
  /** Cursor da próxima página (null: não há mais). */
  nextCursor: z.string().nullable(),
  items: z.array(
    z.object({
      at: z.string(),
      actorName: z.string().nullable(),
      /** O que mudou: dados, premissas, metas ou a escolha como base. */
      kind: z.enum(['SCENARIO', 'PREMISES', 'TARGETS', 'BASE', 'ARCHIVE']),
      summary: z.string(),
    }),
  ),
});
export type ScenarioHistory = z.infer<typeof scenarioHistorySchema>;
